import {createAsyncThunk} from '@reduxjs/toolkit'

import {isApiError} from '@shared/helpers/api'

import {OrderSheetPageSet} from '@web/core/constants'
import {AccountView} from '@web/slices/account/slice'
import {CardView, convertOverseaEasyCard} from '@web/slices/card/slice'
import {CommonState, pageSetInfoSelector} from '@web/slices/common/slice'

import api from './apis'
import {BankEasyPaysReloadResponse, CardEasyPaysReloadResponse, PaymentError} from './interfaces'
import {convertAccount, convertCard} from './utils'

type RootState = {
    common: CommonState
}

/** 계좌간편결제 정보 재조회 */
export const getBankEasyPays = createAsyncThunk<
    {
        /** 계좌 등록 여부 */
        data: BankEasyPaysReloadResponse
        /** 간편 계좌 목록 */
        bankEasyPays: AccountView[]
        /** 충전 계좌 목록 */
        recharges: AccountView[]
        /** 계좌 등록 여부 */
        isRegistered?: boolean
    },
    {
        /** 계좌 등록 여부 */
        isRegistered?: boolean
    },
    {
        state: RootState
        rejectValue: PaymentError
    }
>('paymentMethod/getBankEasyPays', async ({isRegistered}, {getState, rejectWithValue}) => {
    try {
        const state = getState()
        const {orderSheetPageSet} = pageSetInfoSelector(state)
        const data = await api.getBankEasyPays({
            pageSet: orderSheetPageSet ?? OrderSheetPageSet.MALL,
            orderSheetId: state.common.seqID,
        })

        return {
            data,
            recharges: convertAccount(data.recentChargedAccountFirstBankEasyPays),
            bankEasyPays: convertAccount(data.recentUsedFirstBankEasyPays),
            isRegistered,
        }
    } catch (e) {
        if (isApiError(e)) {
            return rejectWithValue(e.response?.data?.result)
        }
        throw e
    }
})

/** 카드간편결제 정보 재조회 */
export const getCardEasyPays = createAsyncThunk<
    {
        data: CardEasyPaysReloadResponse
        /** 간편 계좌 목록 */
        cardEasyPays: CardView[]
        shouldCardPromotion: boolean
    },
    void,
    {
        state: RootState
        rejectValue: PaymentError
    }
>('paymentMethod/getCardEasyPays', async (_, {getState, rejectWithValue}) => {
    try {
        const state = getState()
        const {orderSheetPageSet} = pageSetInfoSelector(state)
        const data = await api.getCardEasyPays({
            pageSet: orderSheetPageSet ?? OrderSheetPageSet.MALL,
            orderSheetId: state.common.seqID,
        })
        return {
            data,
            cardEasyPays: [
                ...convertCard(data.cardEasyPays),
                ...convertOverseaEasyCard(data?.overseasCardEasyPays ?? []),
            ],
            shouldCardPromotion: orderSheetPageSet === OrderSheetPageSet.SELLER,
        }
    } catch (e) {
        if (isApiError(e)) {
            return rejectWithValue(e.response?.data?.result)
        }
        throw e
    }
})
